import { authenticatedApi } from '../Api/api';
import { GooglePlaceResult } from '../models/incidentInBusiness';

// Mapbox token from existing project
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiZGF2aWRhZmZpbmlnbyIsImEiOiJjbGtjYXdweTQwbTR1M3FtdGl1Mm5ncXE3In0.mDm0tCW7GtHNQadtA6RqXQ';

export interface LocationResult {
  address: string;
  streetAddress?: string; // Rue uniquement, sans ville/CP/pays
  latitude: number;
  longitude: number;
  postalCode?: string;
  city?: string;
  region?: string;
  country?: string;
  placeId?: string; // Google Place ID (optionnel, pour déduplication)
}

export interface MapboxFeature {
  id: string;
  type: string;
  place_type: string[];
  relevance: number;
  properties: any;
  text: string;
  place_name: string;
  center: [number, number]; // [longitude, latitude]
  geometry: {
    type: string;
    coordinates: [number, number];
  };
  context?: Array<{
    id: string;
    text: string;
    wikidata?: string;
    short_code?: string;
  }>;
}

class GeocodingService {
  private accessToken: string;

  constructor() {
    this.accessToken = MAPBOX_ACCESS_TOKEN;
  }

  /**
   * Forward geocoding: Convert address to coordinates
   */
  async searchAddress(query: string, options?: {
    country?: string;
    proximity?: [number, number];
    types?: string[];
    limit?: number;
  }): Promise<LocationResult[]> {
    try {
      const params = new URLSearchParams({
        access_token: this.accessToken,
        ...(options?.country && { country: options.country }),
        ...(options?.proximity && { proximity: options.proximity.join(',') }),
        ...(options?.types && { types: options.types.join(',') }),
        ...(options?.limit && { limit: options.limit.toString() }),
      });

      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?${params}`
      );

      if (!response.ok) {
        throw new Error(`Geocoding failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.features || data.features.length === 0) {
        return [];
      }

      return data.features.map((feature: MapboxFeature) => 
        this.extractLocationData(feature)
      );
    } catch (error) {
      console.error('Error in searchAddress:', error);
      throw error;
    }
  }

  /**
   * Reverse geocoding: Convert coordinates to address
   */
  async getAddressFromCoordinates(
    longitude: number, 
    latitude: number,
    options?: {
      types?: string[];
      language?: string;
    }
  ): Promise<LocationResult | null> {
    try {
      const params = new URLSearchParams({
        access_token: this.accessToken,
        ...(options?.types && { types: options.types.join(',') }),
        ...(options?.language && { language: options.language }),
      });

      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?${params}`
      );

      if (!response.ok) {
        throw new Error(`Reverse geocoding failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.features || data.features.length === 0) {
        return null;
      }

      // Return the most relevant result
      return this.extractLocationData(data.features[0]);
    } catch (error) {
      console.error('Error in getAddressFromCoordinates:', error);
      throw error;
    }
  }

  /**
   * Extract relevant location data from Mapbox feature
   */
  private extractLocationData(feature: MapboxFeature): LocationResult {
    const context = feature.context || [];

    // Extract components from context
    const postalCode = context.find(c => c.id.includes('postcode'))?.text;
    const city = context.find(c => c.id.includes('place'))?.text;
    const region = context.find(c => c.id.includes('region'))?.text;
    const country = context.find(c => c.id.includes('country'))?.text;

    // Extraire le numéro et le nom de rue depuis place_name
    // Format typique : "85 Rue Des Mangues, 97400 Saint-Denis, Réunion, France"
    const addressParts = feature.place_name.split(',');
    const streetWithNumber = addressParts[0]?.trim() || feature.text;

    return {
      address: feature.place_name, // Adresse complète
      streetAddress: streetWithNumber, // Rue avec numéro (ex: "85 Rue Des Mangues")
      latitude: feature.center[1],
      longitude: feature.center[0],
      postalCode,
      city,
      region,
      country,
    };
  }

  /**
   * Get current user position using browser geolocation API
   */
  async getCurrentPosition(): Promise<{ latitude: number; longitude: number }> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          let errorMessage = 'Unable to get your location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  }

  /**
   * Search for garages using Google Places API (via backend)
   */
  async searchGarage(
    query: string,
    location?: { lat: number; lng: number },
    radius?: number
  ): Promise<GooglePlaceResult[]> {
    try {
      const response = await authenticatedApi.post('/sav-v2/geocoding/search-garage', {
        query,
        location,
        radius: radius || 5000,
      });

      if (response.data?.success && response.data?.results) {
        return response.data.results;
      }

      return [];
    } catch (error) {
      console.error('Error searching garage:', error);
      throw error;
    }
  }

  /**
   * Search for generic businesses using Google Places API (via backend)
   */
  async searchBusiness(
    query: string,
    location?: { lat: number; lng: number },
    radius?: number
  ): Promise<GooglePlaceResult[]> {
    try {
      const response = await authenticatedApi.post('/sav-v2/geocoding/search-business', {
        query,
        location,
        radius: radius || 5000,
      });

      if (response.data?.success && response.data?.results) {
        return response.data.results;
      }

      return [];
    } catch (error) {
      console.error('Error searching business:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const geocodingService = new GeocodingService();